import React, { useEffect } from 'react';

// Components
import { Button } from '@capintel/component-lib/button';
import { TextArea } from '@/views/components/inputs';
import { withErrorBoundary } from '@/views/components/errors';

// Libraries
import { connect } from 'react-redux';

// Redux
import { saveContact } from '@/redux/modules/contacts';
import { ReduxState } from '@/redux/modules/types';

// Types
import GenericContact from '@/types/generic-contact';

// Utils
import notification from '@/utils/notification';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { truncateString } from '@/utils/objects';

type DispatchProps = {
  saveContact: (contact: Partial<GenericContact>) => Promise<GenericContact>;
};

type Props = DispatchProps & {
  contact: GenericContact;
};

interface NoteState {
  isDirty: boolean;
  isFocused: boolean;
  isSaving: boolean;
  note: string;
}

const ContactNotes = (props: Props) => {
  const { contact, saveContact } = props;
  const [noteState, setNoteState] = React.useState<NoteState>({
    note: contact.note || '',
    isDirty: false,
    isFocused: false,
    isSaving: false,
  });

  useEffect(() => {
    setNoteState(prevState => ({
      ...prevState,
      note: contact.note || '',
      isDirty: false,
    }));
  }, [contact]);

  const onChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    setNoteState(prevState => ({
      ...prevState,
      note: value,
      isDirty: value !== contact.note,
    }));
  };

  const onSave = () => {
    setNoteState(prevState => ({ ...prevState, isSaving: true }));
    saveContact({ ...contact, note: noteState.note })
      .then(() => {
        setNoteState(prevState => ({
          ...prevState,
          isSaving: false,
          isDirty: false,
          isFocused: false,
        }));
        notification.success(formatMessage('Contacts.ContactTabs.Notes.Notifications.Success'));
      })
      .catch(error => {
        notification.error(formatMessage('Contacts.ContactTabs.Notes.Notifications.Failed'));
        setNoteState(prevState => ({ ...prevState, isSaving: false }));
      });
  };

  const onCancel = () => {
    setNoteState(prevState => ({
      ...prevState,
      note: contact.note || '',
      isDirty: false,
      isFocused: false,
    }));
  };

  const handleFocus = () => {
    setNoteState(prevState => ({ ...prevState, isFocused: true }));
  };

  const handleBlur = () => {
    setNoteState(prevState => ({
      ...prevState,
      isFocused: prevState.isDirty,
    }));
  };

  return (
    <div className="contact-notes">
      <div className="contact-notes-header">
        <FormattedMessage
          data-private
          data-dd-privacy="mask"
          id="Contacts.ContactTabs.Notes.Header"
          values={{ name: truncateString(contact?.fname || '', 80) }}
        />
      </div>

      <div className="contact-notes-body">
        <TextArea onBlur={handleBlur} onChange={onChange} onFocus={handleFocus} value={noteState.note} />
      </div>

      <div className="contact-notes-footer">
        <Button variant="primary" hidden={!noteState.isFocused} loading={noteState.isSaving} onClick={onSave} disabled={!noteState.isDirty}>
          <FormattedMessage id="Common.Save" />
        </Button>
        <Button variant="secondary" hidden={!noteState.isFocused} onClick={onCancel}>
          <FormattedMessage id="Common.Cancel" />
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  contact: state.contacts.current?.contact,
});

export default connect(mapStateToProps, {
  saveContact,
})(withErrorBoundary(ContactNotes));
